<template>
  <v-row no-gutters>
    <v-col v-if="appStore.isCustomer" class="delayEl">
      <CustomerAlert />
    </v-col>
    <v-col v-else>
      <div id="cash-report-list" :class="{ 'page-fullscreen': pageStore.isFullScreen }">
        <v-card tile :class="$vuetify.breakpoint.mdAndDown ? null : 'rounded-t-xl'">
          <PageHeader>
            <template #action v-if="userData.role === 'SUBE'">
              <v-btn
                rounded
                color="warning"
                class="ms-2 text-capitalize"
                depressed
                @click="store.openAddDialog"
              >
                Yeni Ekle
              </v-btn>
            </template>
          </PageHeader>

          <v-data-table
            :headers="tableColumns"
            :items="tableItems"
            :loading="tableLoading"
            :height="pageStore.tableHeight"
            loader-height="2"
            :mobile-breakpoint="100"
            fixed-header
            hide-default-footer
            :options.sync="tableOptions"
            :server-items-length="tableTotalRec"
            :disable-items-per-page="true"
            :items-per-page="20"
          >
            <!-- Load More -->
            <template v-slot:body.append v-if="loadMoreLoading">
              <tr>
                <td :colspan="tableColumns.length">
                  <div class="text-center" v-intersect.quiet="loadMore">
                    <v-progress-circular
                      v-if="tableItems.length > 0"
                      indeterminate
                      color="primary"
                    ></v-progress-circular>
                  </div>
                </td>
              </tr>
            </template>

            <!-- Arama Alanı -->
            <template v-slot:body.prepend>
              <TableSearch
                v-model="searchModel"
                :status-list="getExpenseStatusList"
                :empty-search-count="tableEmptySearch"
                @input="handleSearchChange"
              />
            </template>

            <!-- ID -->
            <template #[`item.id`]="{ item }">
              <TableCells :item="item" type="id" />
            </template>

            <!-- Statu -->
            <template #[`item.status_txt`]="{ item }">
              <TableCells :item="item" type="status_txt" />
            </template>

            <!-- Hazırlayan -->
            <template #[`item.created_by`]="{ item }">
              <TableCells :item="item" type="created_by" />
            </template>

            <!-- Tutar   -->
            <template #[`item.genamount`]="{ item }">
              <TableCells :item="item" type="genamount" />
            </template>

            <!-- Kalem -->
            <template #[`item.kalem_sayisi`]="{ item }">
              <TableCells :item="item" type="kalem_sayisi" />
            </template>

            <!-- İşlem Tarihi -->
            <template #[`item.created_date`]="{ item }">
              <TableCells :item="item" type="created_date" />
            </template>

            <!-- Hareket -->
            <template #[`item.actions`]="{ item }">
              <TableCells
                :item="item"
                type="actions"
                @image-upload="store.openImageDialog"
                @start-process="store.startProcessFn"
              >
                <template #data-view>
                  <v-btn @click="store.openViewDialog(item)" block depressed color="chip" small>
                    <div class="text-capitalize">Görüntüle</div>
                  </v-btn>
                </template>
                <template #data-edit v-if="item.status === 'FRST1'">
                  <VTooltip left color="warning">
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="warning"
                        class="me-2"
                        v-bind="attrs"
                        v-on="on"
                        size="x-small"
                        @click="store.openEditDialog(item)"
                      >
                        <v-icon size="21">{{ icons.mdiPencil }}</v-icon>
                      </v-btn>
                    </template>
                    <span>Düzenle</span>
                  </VTooltip>
                </template>
                <template #data-process v-if="item.status === 'FRSaT'">
                  <VTooltip left color="error">
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="error"
                        v-bind="attrs"
                        v-on="on"
                        size="x-small"
                        @click="store.openEditDialog(item)"
                      >
                        <v-icon size="21">{{ icons.mdiCloseCircle }}</v-icon>
                      </v-btn>
                    </template>
                    <span>Reddet</span>
                  </VTooltip>
                  <VTooltip left color="success">
                    <template #activator="{ on, attrs }">
                      <v-btn
                        icon
                        color="success"
                        v-bind="attrs"
                        v-on="on"
                        size="x-small"
                        @click="store.openEditDialog(item)"
                      >
                        <v-icon size="21">{{ icons.mdiCheckCircle }}</v-icon>
                      </v-btn>
                    </template>
                    <span>Onayla</span>
                  </VTooltip>
                </template>
              </TableCells>
            </template>
          </v-data-table>
        </v-card>
      </div>
      <ImageAdd />
      <DataAddEdit @saveData="handleAddEdit" />
      <DataView />
    </v-col>
  </v-row>
</template>

<script>
import { ref, onMounted, onUnmounted, watch } from "vue-demi";
import { mdiPencil, mdiCheckCircle, mdiCloseCircle } from "@mdi/js";

// Pinia
import { useAppStore } from "@/stores/app";
import { useCashReportStore } from "./store";

// Composables
import { useExpenseTable } from "./composables/useExpenseTable";
import { usePageSettings } from "./composables/usePageSettings";

// Components
import PageHeader from "@/components/PageHeader/Index.vue";
import DataAddEdit from "./DataAddEdit.vue";
import DataView from "./DataView.vue";
import ImageAdd from "./ImageAdd.vue";
import TableCells from "./components/TableCells.vue";
import TableSearch from "./components/TableSearch.vue";
import CustomerAlert from "@/components/CustomerAlert.vue";
import { VTooltip } from "vuetify/lib";

export default {
  components: {
    PageHeader,
    DataAddEdit,
    DataView,
    ImageAdd,
    TableCells,
    TableSearch,
    CustomerAlert,
  },
  setup() {
    const store = useCashReportStore();
    const appStore = useAppStore();
    const userData = JSON.parse(localStorage.getItem("userData"));

    // Sayfa ayarları
    const { pageStore } = usePageSettings();

    // Tablo işlemleri
    const {
      tableLoading,
      tableItems,
      tableTotalRec,
      tableColumns,
      loadMore,
      loadMoreLoading,
      tableFetch,
      tableEmptySearch,
      tableOptions,
      tableSearchID,
      tableSearchStatu,
      tableSearchUser,
      tableSearchFormDate,
      handleAddEdit,
      getExpenseStatusList,
      initTable,
      setupTableWatchers,
      startPage,
      icons,
    } = useExpenseTable();

    // Arama değişkenlerini birleştiriyoruz
    const searchModel = ref({
      id: "",
      statu: "",
      user: "",
      formDate: "",
    });

    // Arama değişikliklerini işliyoruz
    const handleSearchChange = (searchData) => {
      tableSearchID.value = searchData.id;
      tableSearchStatu.value = searchData.statu;
      tableSearchUser.value = searchData.user;
      tableSearchFormDate.value = searchData.formDate;
    };

    onMounted(async () => {
      if (appStore.isCustomer) return;
      await initTable();
      setupTableWatchers();
    });

    // AppStore izle
    watch(
      () => [appStore.selectedCustomer],
      (val) => {
        if (!startPage.value) {
          loadMoreLoading.value = false;
          tableLoading.value = true;
          tableItems.value = [];
          tableFetch();
        } else {
          startPage.value = false;
        }
      }
    );

    return {
      // Stores
      store,
      pageStore,
      userData,
      appStore,

      // Table
      tableColumns,
      tableItems,
      tableLoading,
      loadMore,
      loadMoreLoading,
      tableTotalRec,
      tableEmptySearch,
      tableOptions,

      // Search
      searchModel,
      handleSearchChange,

      // Misc
      handleAddEdit,
      getExpenseStatusList,

      // Icons
      icons: {
        mdiPencil,
        mdiCheckCircle,
        mdiCloseCircle,
      },
    };
  },
};
</script>
